import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Column, Row, Text} from "react-vcomponents";
import {ShowVMenu, VMenuItem} from "react-vmenu";
import {GetMapObjects} from "../../../../../../Server/DB/mapObjects.js";
import {ResolveObjPosInWorld} from "../../../../../../Server/DB/mapObjects/$mapObject.js";
import {InterpolateConfig, ObjPath, PathTransform} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {liveSkin} from "../../../../../../Utils/Styles/StyleManager.js";
import {Vec2} from "../../../../../../Utils/ThreeJS/VectorStructs.js";
import {PathEditorUI_SharedProps} from "../PathEditorUI.js";
import {ShowMessageBox} from "react-vmessagebox";
import {MapObject} from "../../../../../../Server/DB/mapObjects/@MapObject.js";
import {ResolveOriginPathForPath} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath/ObjPathResolvers.js";
import {DMap} from "../../../../../../Server/DB/maps/@Map.js";
import {ExtrudeUI} from "../TransformUIs/ExtrudeUI.js";
import {InterpolateUI} from "../TransformUIs/InterpolateUI.js";

export const PathEditorTab_Transforms = observer_mgl((props: {} & PathEditorUI_SharedProps)=>{
	const {map, obj, path, index, ChangeObj, Change} = props;
	const objPos = ResolveObjPosInWorld(obj);
	const allMapObjects = GetMapObjects(map.id);
	const pathIdDuplicate = !!path.path_id && (allMapObjects ?? []).some(obj2=>{
		return (obj2.paths ?? []).some(path2=>{
			if (obj2.id === obj.id && obj2.paths!.indexOf(path2) === index) return false; // skip self
			return path2.path_id === path.path_id;
		});
	});

	return <>
		<Row mt={5}>
			<Text>Transforms:</Text>
			<Button ml={5} mdIcon="plus" onClick={e=>{
				const buttonRect = (e.target as HTMLElement).getBoundingClientRect();
				ShowVMenu(
					{pos: new Vec2(buttonRect.left, buttonRect.top + buttonRect.height)},
					<>
						<VMenuItem text="Extrude" style={liveSkin.Style_VMenuItem()} onClick={()=>{
							Change(a=>a.transforms = (a.transforms ?? []).concat(new PathTransform({
								extrude: {distance: 1, sides: ["out"], meshGen: "eachCopySeparate"},
							})));
						}}/>
						<VMenuItem text="Interpolate" style={liveSkin.Style_VMenuItem()} onClick={()=>{
							Change(a=>a.transforms = (a.transforms ?? []).concat(new PathTransform({
								interpolate: new InterpolateConfig({}),
							})));
						}}/>
					</>,
				);
			}}/>
		</Row>
		{(path.transforms ?? []).map((transform, tIndex)=>{
			return <TransformEditorUI key={tIndex} {...{map, obj, Change: ChangeObj, pathIndex: index, tIndex}}/>;
		})}
	</>;
});

export type TransformUI_SharedProps = {
	map: DMap;
	obj: MapObject;
	path: ObjPath;
	/** Resolve-origin path's `curve_closed` flag (closed→in/out sides; open→left/right). */
	sourceClosed: boolean;
	/** Local changer scoped to this transform entry. */
	Change: (transformChanger: (t: PathTransform)=>any)=>any;
};

const TransformEditorUI = observer_mgl((props: {map: DMap, obj: MapObject, Change: (changer: (obj: any)=>any)=>void, pathIndex: number, tIndex: number})=>{
	const {map, obj, Change: Change_Outer, pathIndex, tIndex} = props;
	const path = obj.paths[pathIndex];
	const transform = path.transforms[tIndex];
	// Local changer scoped to this transform entry.
	const Change = (transformChanger: (t: PathTransform)=>any)=>{
		return Change_Outer(a=>transformChanger(a.paths[pathIndex].transforms[tIndex]));
	};
	const interp = transform.interpolate;
	// Determine valid side options based on the source path's closed/open topology.
	const originPath = ResolveOriginPathForPath(path, obj);
	if (originPath == null) {
		return <Column mt={3} ml={20} style={{background: "rgba(255,255,255,.15)", padding: 5, borderRadius: 5}}>
			{`Source-path chain is invalid. Resolve that first.`}
		</Column>;
	}

	const sourceClosed = !!originPath?.path.curve_closed;
	const sharedProps: TransformUI_SharedProps = {map, obj, path, sourceClosed, Change};

	return (
		<Column mt={3} ml={20} style={{background: "rgba(255,255,255,.15)", padding: 5, borderRadius: 5}}>
			<Row>
				<Text style={{fontWeight: "bold"}}>{tIndex + 1}: {interp ? "Interpolate" : "Extrude"}</Text>
				<Row ml="auto" style={{alignItems: "center"}}>
					<Text mr={5}>Enabled</Text>
					<CheckBox value={!transform.disabled} onChange={val=>Change(a=>a.disabled = val ? undefined : true)}/>
				</Row>
				<Button ml={5} mdIcon="delete" title="Remove transform" onClick={()=>{
					ShowMessageBox({
						title: "Delete transform?", cancelButton: true,
						message: `Permanently delete this transform?`,
						onOK: ()=>{
							Change_Outer(a=>{
								a.paths[pathIndex].transforms = a.paths[pathIndex].transforms.filter((_: any, i: number)=>i !== tIndex);
							});
						},
					});
				}}/>
			</Row>

			{interp ? <InterpolateUI {...{...sharedProps, transform}}/> : <ExtrudeUI {...{...sharedProps, transform}}/>}
		</Column>
	);
});